import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink, useNavigate } from 'react-router-dom';


import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Button,
  Link,
  useToast,
  Card,
  CardBody,
  Icon,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Alert,
  AlertIcon,
  Image
} from '@chakra-ui/react';
import { FaEye, FaEyeSlash, FaServer, FaGoogle, FaGithub, FaEnvelope } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import apiService from '../services/api';
import Turnstile from "react-turnstile";
import { TURNSTILE_SITE_KEY } from "../config";
import { UNDER_CONSTRUCTION, ALLOWED_EMAIL } from '../App';


const SignIn = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { login } = useAuth();
  const [turnstileToken, setTurnstileToken] = useState("");

  // Forgot password modal state
  const { isOpen: isForgotOpen, onOpen: onForgotOpen, onClose: onForgotClose } = useDisclosure();
  const [forgotEmail, setForgotEmail] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [resetError, setResetError] = useState(null);
  


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast({
        title: 'Error',
        description: 'Please fill in all fields',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (UNDER_CONSTRUCTION && email.trim().toLowerCase() !== ALLOWED_EMAIL) {
      toast({
        title: 'Temporarily Unavailable',
        description: 'Sign-in is temporarily disabled while we improve SRS. Please check back soon.',
        status: 'warning',
        duration: 6000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    
    try {
      await login(email, password, turnstileToken);
      navigate('/dashboard');
    } catch (error) {
      //setTurnstileToken("");  // reset captcha token      

      toast({
        id: "login-error",
        title: 'Sign In Failed',
        description: 'Invalid email or password',
        status: 'error',
        duration: 5000,
        isClosable: true
        //onCloseComplete: () => setTurnstileToken("") // reset only after toast disappears
      });

      await new Promise((resolve) => setTimeout(resolve, 10000));


    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!forgotEmail.trim()) {
      setResetError('Please enter your email address');
      return;
    }

    setIsSendingReset(true);
    setResetError(null);

    try {
      await apiService.resetPasswordRequest(forgotEmail);
      
      toast({
        title: 'Reset Link Sent',
        description: `A password reset link has been sent to ${forgotEmail}. Please check your email and follow the instructions.`,
        status: 'success',
        duration: 8000,
        isClosable: true,
      });
      
      // Reset form and close modal
      setForgotEmail('');
      onForgotClose();
    } catch (error) {
      setTurnstileToken("");  // reset captcha token      
      setResetError('Failed to send password reset email. Please check your email address and try again.');
    } finally {
      setIsSendingReset(false);
    }
  };

  const openForgotModal = () => {
    setForgotEmail(email); // Pre-fill with current email if available
    setResetError(null);
    onForgotOpen();
  };

  const handleGoogleSignIn = () => {
    // Handle Google sign in
    toast({
      title: 'Coming Soon',
      description: 'Google Sign In will be available soon!',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleGithubSignIn = () => {
    // Handle GitHub sign in
    toast({
      title: 'Coming Soon',
      description: 'GitHub Sign In will be available soon!',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center" >
      <Helmet>
        <title>Sign In | SRS</title>
        <meta name="description" content="Sign in to SRS to run GPU-accelerated Scale-Resolving Simulations in the cloud." />
        <link rel="canonical" href="https://srs.numericalai.net/signin" />
        <meta property="og:title" content="Sign In | SRS" />
        <meta property="og:description" content="Sign in to SRS to run GPU-accelerated Scale-Resolving Simulations in the cloud." />
        <meta property="og:url" content="https://srs.numericalai.net/signin" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SRS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Sign In | SRS" />
        <meta name="twitter:description" content="Sign in to SRS to run GPU-accelerated Scale-Resolving Simulations in the cloud." />
      </Helmet>
      <Container maxW="md">
        <VStack spacing={8}>
          {/* Header */}
          <VStack spacing={4} textAlign="center">
            <HStack 
              spacing={1} 
              cursor="pointer" 
              onClick={() => navigate('/')} 
              _hover={{ opacity: 0.8 }} 
              transition="opacity 0.2s"
              borderRadius="md"
              p={2}
              _active={{ transform: "scale(0.98)" }}
            >
              <Image
                src="/simple-logo.png"
                alt="SRS Logo"
                boxSize={14}
                objectFit="contain"
              />
              <Box>
                <Heading size="xl" color="brand.500">SRS</Heading>
                <Text color="gray.600">Scale-Resolving Simulation at Scale</Text>
              </Box>
            </HStack>
          </VStack>

          {/* Sign In Card */}
          <Card w="full" shadow="lg">
            <CardBody p={8}>
              <VStack spacing={6}>
                <VStack spacing={2} textAlign="center">
                  <Heading as="h1" size="lg">Welcome Back</Heading>
                  <Text color="gray.600">Sign in to your account to continue</Text>
                </VStack>

                <form onSubmit={handleSubmit}>
                  <VStack spacing={6}>
                 
                  <Turnstile
                            sitekey={TURNSTILE_SITE_KEY}
                            onVerify={(token) => setTurnstileToken(token)}
                            onExpire={() => setTurnstileToken("")}
                          />
                                    
                    <FormControl isRequired>
                      <FormLabel>Email Address</FormLabel>
                      <Input
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        size="lg"
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Password</FormLabel>
                      <InputGroup size="lg">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                        />
                        <InputRightElement>

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </Button>
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>

                    <Button
                      type="submit"
                      colorScheme="brand"
                      size="lg"
                      w="full"
                      isLoading={isLoading}
                      loadingText="Signing In..."
                      disabled={!turnstileToken}   // 👈 disable if no token
                    >
                      Sign In
                    </Button>
                  </VStack>
                </form>

                <VStack spacing={4} mt={4}>
                  <Button
                    onClick={openForgotModal}
                    variant="link"
                    colorScheme="brand"
                    fontSize="sm"
                    isDisabled={!turnstileToken}
                  >
                    Forgot your password?
                  </Button>

                  <Divider />

                  {/*
                  <VStack spacing={3} w="full">
                    <Button
                      w="full"
                      variant="outline"
                      leftIcon={<FaGoogle />}
                      onClick={handleGoogleSignIn}
                    >
                      Continue with Google
                    </Button>
                    <Button
                      w="full"
                      variant="outline"
                      leftIcon={<FaGithub />}
                      
                      onClick={handleGithubSignIn}
                    >
                      Continue with GitHub
                    </Button>
                  </VStack>
                  */}
                </VStack>
              </VStack>
            </CardBody>
          </Card>

          {/* Sign Up Link */}
          <HStack spacing={1}>
            <Text color="gray.600">Don't have an account?</Text>
            <Link as={RouterLink} to="/create-user" color="brand.500" fontWeight="semibold">
              Sign up
            </Link>
          </HStack>
        </VStack>
      </Container>

      {/* Forgot Password Modal */}
      <Modal isOpen={isForgotOpen} onClose={onForgotClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack spacing={3}>
              <Icon as={FaEnvelope} color="brand.500" boxSize={6} />
              <Text>Reset Password</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleForgotPassword}>
            <ModalBody>
              {resetError && (
                <Alert status="error" borderRadius="md" mb={4}>
                  <AlertIcon />
                  {resetError}
                </Alert>
              )}
              
              <VStack spacing={4}>
                <Text color="gray.600" textAlign="center">
                  Enter your email address and we'll send you a link to reset your password.
                </Text>
                
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="semibold">Email Address</FormLabel>
                  <Input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => {
                      setForgotEmail(e.target.value);
                      if (resetError) setResetError(null);
                    }}
                    placeholder="Enter your email address"
                    size="md"
                  />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onForgotClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="brand"
                isLoading={isSendingReset}
                loadingText="Sending..."
              >
                Send Reset Link
              </Button>
            </ModalFooter>
          </form>

        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SignIn; 